import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import '../MyCourses/MyCourses.css';
import InstaTopbar from '../InstaTopbar/InstaTopbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEye, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { FaHistory } from 'react-icons/fa';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import useBlockCheck from '../../hooks/useBlockCheck';



const MyCourses = () => {
  useBlockCheck('instructor');

  // TEMPORARY PERIOD: platform is offering all courses as FREE at the start.
  // Set this to `false` to let instructors manage pricing normally.
  const PLATFORM_TEMPORARY_FREE_COURSES = true;

  const { token } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [showDescription, setShowDescription] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'published';
  const [view, setView] = useState('published');
  const [paymentDetails, setPaymentDetails] = useState({
    actualPrice: '',
    offerPrice: '',
    currency: 'INR',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [setPublishedCourses] = useState([]);
  const [course, setCourse] = useState(null);
  const [selectedCourseTotalHours, setSelectedCourseTotalHours] = useState(0);
  const [showLaunchBanner, setShowLaunchBanner] = useState(true);
  const criteriaValues = {  actualValue: 1.5,  offerValue: 1};

  const effectiveIsFree = PLATFORM_TEMPORARY_FREE_COURSES ? true : isFree;
  const lockPricingFields = PLATFORM_TEMPORARY_FREE_COURSES || effectiveIsFree;

  const navigate = useNavigate();

  // Helper function to check if course can be published
  const canPublishCourse = (course) => {
    if (!course) return false;
    
    // Check if course has any lessons
    const hasLessons = course?.sections?.some(section =>
      section.lessons && section.lessons.length > 0,
    );
    
    if (!hasLessons) return false;
    
    // Calculate total video duration in seconds from all lessons
    const calculateTotalVideoDuration = () => {
      if (!course?.sections) return 0;
      return course.sections.reduce((total, section) => {
        if (!section.lessons) return total;
        return total + section.lessons.reduce((sectionTotal, lesson) => {
          return sectionTotal + (lesson.duration || 0);
        }, 0);
      }, 0);
    };

    const totalVideoDurationSeconds = calculateTotalVideoDuration();
    const MINIMUM_DURATION_SECONDS = 180; // 3 minutes = 180 seconds
    const hasMinimumDuration = totalVideoDurationSeconds >= MINIMUM_DURATION_SECONDS;
    
    return hasLessons && hasMinimumDuration;
  };

  // Helper function to get publish button tooltip
  const getPublishTooltip = (course) => {
    if (!course) return 'Course not found';
    
    const hasLessons = course?.sections?.some(section =>
      section.lessons && section.lessons.length > 0,
    );
    
    if (!hasLessons) {
      return 'Add at least one lesson to publish the course';
    }
    
    // Calculate total video duration
    const calculateTotalVideoDuration = () => {
      if (!course?.sections) return 0;
      return course.sections.reduce((total, section) => {
        if (!section.lessons) return total;
        return total + section.lessons.reduce((sectionTotal, lesson) => {
          return sectionTotal + (lesson.duration || 0);
        }, 0);
      }, 0);
    };

    const totalVideoDurationSeconds = calculateTotalVideoDuration();
    const MINIMUM_DURATION_SECONDS = 180;
    
    if (totalVideoDurationSeconds < MINIMUM_DURATION_SECONDS) {
      const minutes = Math.floor(totalVideoDurationSeconds / 60);
      return `Course must have at least 3 minutes of video content. Current duration: ${minutes} minutes`;
    }
    
    return 'Publish course';
  };

  const handlePublish = async (draftId) => {
    // Find the course to validate
    const courseToPublish = drafts.find(c => c._id === draftId);
    
    // Validate before showing confirmation
    if (!canPublishCourse(courseToPublish)) {
      const hasLessons = courseToPublish?.sections?.some(section =>
        section.lessons && section.lessons.length > 0,
      );
      
      if (!hasLessons) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot Publish',
          text: 'Please add at least one lesson. Minimum 3 minutes video is required to publish the course.',
        });
        return;
      }
      
      // Calculate duration for error message
      const calculateTotalVideoDuration = () => {
        if (!courseToPublish?.sections) return 0;
        return courseToPublish.sections.reduce((total, section) => {
          if (!section.lessons) return total;
          return total + section.lessons.reduce((sectionTotal, lesson) => {
            return sectionTotal + (lesson.duration || 0);
          }, 0);
        }, 0);
      };
      
      const totalVideoDurationSeconds = calculateTotalVideoDuration();
      const minutes = Math.floor(totalVideoDurationSeconds / 60);
      
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Publish',
        text: `Course must have at least 3 minutes of video content. Current duration: ${minutes} minutes.`,
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Publish Course?',
      text: 'Once published, you cannot delete this course!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Publish it!',
    });

    if (!result.isConfirmed) return;

    try {
      // 1. Set published = true in CourseDraft
      await axios.put(api.publishDraftCourse(draftId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2. Copy the draft to the Course table
      const response = await axios.post(api.publishCourseFromDraft, { draftId }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.course) {
        const publishedCourse = response.data.course;
        setCourse(publishedCourse);

        toast.success('Course published successfully!');

        // Update drafts to include the newly published course
        setDrafts((prevDrafts) =>
          prevDrafts.filter(draft => draft._id !== draftId).concat(publishedCourse),
        );

        // Redirect to Published Courses
        setView('published'); // Update the view to "published"
      } else {
        toast.error('Failed to publish the course.');
      }
    } catch (error) {
      toast.error('Failed to publish the course.');
    }
  };

  useEffect(() => {
    setView(filter);

    const fetchDrafts = async () => {
      if (!token) {
        console.error('No token found, skipping fetchDrafts');
        return;
      }
      try {
        setIsLoading(true);
        const response = await axios.get(api.getCourseDrafts, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDrafts(response.data.drafts);
      } catch (error) {
        console.error('Draft fetch error:', error?.response?.data || error);
        Swal.fire('Failed to load draft courses', 'Please try again later.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

   
    if (token) {
      fetchDrafts();
      
    }
  }, [filter, token]);

  const handleDelete = async (courseId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this course? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      // Determine if this is a draft or published course
      const course = drafts.find((c) => c._id === courseId);
      if (course && !course.published) {
        // Delete draft course
        await axios.delete(`${api.getCourseDrafts}/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // Delete published course
        await axios.delete(api.deleteCourse(courseId), {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setDrafts((prevDrafts) =>
        prevDrafts.filter((draft) => draft._id !== courseId),
      );

      Swal.fire('Deleted!', 'The course has been deleted.', 'success');
    } catch (error) {
      Swal.fire('Error', 'Failed to delete the course.', 'error');
    }
  };

  const handleCardClick = (courseId) => {
    navigate(`/instructor-course-details/${courseId}`);
  };

  const toggleDescription = (courseId, e) => {
    e.stopPropagation();
    setShowDescription((prev) => (prev === courseId ? null : courseId));
  };

  const handleOpenPaymentModal = async (courseId) => {
    setSelectedCourseId(courseId);
    if (PLATFORM_TEMPORARY_FREE_COURSES) {
      setIsFree(true);
    } else {
      setIsFree(false);
    }

    try {
      const response = await axios.get(api.getCourseDraftById(courseId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      const course = response.data?.draft;

      if (!course) throw new Error('Course not found');

      const totalHours = course.totalHours || 0;
      setSelectedCourseTotalHours(totalHours);

      const defaultActual = Math.round(totalHours * 60 * criteriaValues.actualValue);
      const defaultOffer = Math.round(totalHours * 60 * criteriaValues.offerValue);

      const loadedDetails = {
        actualPrice: course.actualPrice || defaultActual,
        offerPrice: PLATFORM_TEMPORARY_FREE_COURSES ? 0 : (course.offerPrice || defaultOffer),
        currency: course.currency || 'INR',
        isFree: PLATFORM_TEMPORARY_FREE_COURSES ? true : (course.isFree || false),
      };

      setPaymentDetails({
        actualPrice: loadedDetails.actualPrice,
        offerPrice: loadedDetails.offerPrice,
        currency: loadedDetails.currency,
      });

      setIsFree(loadedDetails.isFree);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Failed to fetch course payment details', error);
      toast.error('Failed to load payment info.');
    }
  };

  const handleSetDefaultPrice = () => {
    const totalHours = selectedCourseTotalHours || 0;
    const defaultActual = Math.round(totalHours * 60 * criteriaValues.actualValue);
    const defaultOffer = Math.round(totalHours * 60 * criteriaValues.offerValue);



    setPaymentDetails((prev) => ({
      ...prev,
      actualPrice: defaultActual,
      offerPrice: PLATFORM_TEMPORARY_FREE_COURSES ? 0 : defaultOffer,
    }));
    setIsFree(PLATFORM_TEMPORARY_FREE_COURSES ? true : false);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedCourseId(null);
    if (PLATFORM_TEMPORARY_FREE_COURSES) {
      setIsFree(true);
    } else {
      setIsFree(false);
    }
    setSelectedCourseTotalHours(0);
    setPaymentDetails({ actualPrice: '', offerPrice: '', currency: 'INR' });
  };

  const handleSubmitPayment = async () => {
    const { actualPrice, offerPrice } = paymentDetails;

    if (!effectiveIsFree && (!actualPrice || !offerPrice)) {
      toast.warning('Please enter ActualPrice and offerPrice.');
      return;
    }

    const payload = {
      ...paymentDetails,
      actualPrice: effectiveIsFree ? 0 : paymentDetails.actualPrice,
      offerPrice: effectiveIsFree ? 0 : paymentDetails.offerPrice,
      isFree: effectiveIsFree,
      draftFields: {
        actualPrice: true,
        offerPrice: true,
        currency: true,
        isFree: true,
      },
    };

    try {
      await axios.post(api.addDraftCoursePayment(selectedCourseId), payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Payment info added successfully!');
      handleClosePaymentModal();
    } catch (error) {
      toast.error('Failed to add payment info.');
    }
  };

  const renderCourseDetails = (course) => {
    return (
      <div className="my-co-card-description">
        <p className="my-co-card-description-p">{course.description}</p>
       
      </div>
    );
  };

  const publishedCourses = drafts.filter(course => course.published);
  const draftCourses = drafts.filter(course => !course.published);

  return (
    <div className="my-co-wrapper">
      <InstaTopbar />
      {showLaunchBanner && (
        <div className="my-co-launch-banner" role="status" aria-live="polite">
          <span className="my-co-launch-pill">App Launch Offer</span>
          <span className="my-co-launch-text">
            Dear Instructors, as part of our launch offer, all courses will be available for free for the next 6 months. Thank you for your support.
          </span>
          <button
            type="button"
            className="my-co-launch-close"
            aria-label="Close launch offer banner"
            onClick={() => setShowLaunchBanner(false)}
          >
            ×
          </button>
        </div>
      )}
      <ToastContainer />
   
      <div className="my-co-header">
        <div className="my-co-header-content">
          <h1 className="my-co-title">My Courses</h1>
          <p className="my-co-subtitle">Manage all your courses in one place</p>
        </div>

      </div>
      <div className="my-co-container">
        <div className="add-btn-container">
          <Link to="/add-new-course" className="my-co-add-btn">
            <FontAwesomeIcon icon={faPlus} className="my-co-add-icon" />
            <span>Create New Course</span>
          </Link>
        </div>
        <div className="my-co-view-toggle">
          <button
            className={`my-co-toggle-btn ${view === 'published' ? 'my-co-toggle-active' : ''}`}
            onClick={() => setView('published')}
          >
            Published Courses
          </button>
          <button
            className={`my-co-toggle-btn ${view === 'unpublished' ? 'my-co-toggle-active' : ''}`}
            onClick={() => setView('unpublished')}
          >
            Draft Courses
          </button>
        </div>

        {isLoading ? (
          <div className="my-co-loading">
            <div className="my-co-spinner"></div>
            <p>Loading your courses...</p>
          </div>
        ) : view === 'published' ? (
          <div className="my-co-section">
            <h2 className="my-co-section-title">Published Courses</h2>
            <p className="my-co-section-description">These courses are live and available to students.</p>

            {publishedCourses.length === 0 ? (
              <div className="my-co-empty-state">
                <div className="my-co-empty-icon">
                  <FontAwesomeIcon icon={faEye} />
                </div>
                <h3>No Published Courses</h3>
                <p>You haven't published any courses yet</p>
              </div>
            ) : (
              <div className="my-co-grid">
                {publishedCourses.map((course) => (
                  <div className="my-co-card" key={course._id}>
                    <div className="my-co-card-image-container" onClick={() => handleCardClick(course._id)}>
                      <img
                        src={`${api.img + course.image}`}
                        alt={course.title}
                        className="my-co-card-image"
                      />
                      <div className="my-co-card-badge">Published</div>
                    </div>
                    <div className="my-co-card-content">
                      {/* Status banner for deletion workflow */}
                      {course.deletionRequested && course.instructorResponse === 'requested_update' && (
                        <div className="my-co-status-banner my-co-status-update">
                          You requested update for this course.
                          {course.updateDeadline && (
                            <span className="my-co-status-deadline">
                              {' '}Deadline: {new Date(course.updateDeadline).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                      {course.deletionRequested && course.instructorResponse === 'confirmed' && (
                        <div className="my-co-status-banner my-co-status-delete">
                          You confirmed deletion for this course. Admin can delete it at any time.
                        </div>
                      )}
                      <h3 className="my-co-card-title" onClick={() => handleCardClick(course._id)}>
                        {course.title}
                      </h3>

                      <div className="my-co-card-actions">
                        <button
                          className="my-co-action-btn my-co-description-btn"
                          onClick={(e) => toggleDescription(course._id, e)}
                        >
                          <FontAwesomeIcon icon={faEye} /> Details
                        </button>
                        
          
                        <span className="my-co-stat-value"> Students:{course.enrolledStudentsCount || 0}</span>
       
      
                      </div>
                      {showDescription === course._id && renderCourseDetails(course)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="my-co-section">
            <h2 className="my-co-section-title">Draft Courses</h2>
            <p className="my-co-section-description">These courses are not yet published to students.</p>

            {draftCourses.length === 0 ? (
              <div className="my-co-empty-state">

                <Link to="/add-new-course" >
                  <div className="my-co-empty-icon">
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </div>
                  <h3>No Draft Courses</h3>
                  <p>Create a new course to get started</p>
                </Link>
              </div>
            ) : (
              <div className="my-co-grid">
                {draftCourses.map((course) => (
                  <div className="my-co-card my-co-card-draft" key={course._id}>
                    <div className="my-co-card-image-container" onClick={() => handleCardClick(course._id)}>
                      <img
                        src={`${api.img + course.image}`}
                        alt={course.title}
                        className="my-co-card-image"
                      />
                      <div className="my-co-card-badge">Draft</div>
                    </div>
                    <div className="my-co-card-content">
                      <h3 className="my-co-card-title" onClick={() => handleCardClick(course._id)}>
                        {course.title}
                      </h3>
                      <div className="my-co-card-actions">
                        <div className="my-co-publish-btn-wrapper">
                          <button
                            className={`my-co-action-btn my-co-publish-btn ${!canPublishCourse(course) ? 'disabled' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePublish(course._id);
                            }}
                            disabled={!canPublishCourse(course)}
                          >
                            Publish
                          </button>
                          {!canPublishCourse(course) && (
                            <span className="my-co-tooltip-red">{getPublishTooltip(course)}</span>
                          )}
                        </div>
                        <button
                          className="my-co-action-btn my-co-payment-btn"
                          onClick={(e) => handleOpenPaymentModal(course._id, e)}
                        >
                          Pricing
                        </button>
                        <button
                          className="my-co-action-btn my-co-description-btn"
                          onClick={(e) => toggleDescription(course._id, e)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          className="my-co-action-btn my-co-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(course._id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      {showDescription === course._id && renderCourseDetails(course)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="button-container">
     
              <button className="add-pay-cancel-btn" onClick={handleClosePaymentModal}>×</button>
     
     
            </div>
            <h3 className="price-info">Price Info</h3>
     
            <label>Currency:</label>
            <select
              value={paymentDetails.currency}
              onChange={(e) =>
                setPaymentDetails({ ...paymentDetails, currency: e.target.value })
              }
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
     
            <div className="checkbox-container">
              <input
                className="isFree-checkbox"
                type="checkbox"
                checked={effectiveIsFree}
                disabled={PLATFORM_TEMPORARY_FREE_COURSES}
                readOnly={PLATFORM_TEMPORARY_FREE_COURSES}
                onChange={(e) => {
                  if (PLATFORM_TEMPORARY_FREE_COURSES) return;
                  setIsFree(e.target.checked);
                }}
              />
              <span className="isFree-span">This is a Free Course (Offer Price set to 0)</span>
            </div>
     
            {!PLATFORM_TEMPORARY_FREE_COURSES && (
              <>
                <label>Actual Price:</label>
                <input
                  type="number"
                  placeholder="Enter actual price"
                  value={paymentDetails.actualPrice}
                  onChange={(e) =>
                    setPaymentDetails({ ...paymentDetails, actualPrice: e.target.value })
                  }
                />
     
                <label>Offer Price:</label>
                <input
                  type="number"
                  placeholder="Enter offer price"
                  value={isFree ? 0 : paymentDetails.offerPrice}
                  disabled={isFree}
                  onChange={(e) =>
                    setPaymentDetails({ ...paymentDetails, offerPrice: e.target.value })
                  }
                />
              </>
            )}

            {PLATFORM_TEMPORARY_FREE_COURSES && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: '#b91c1c',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #f69e9e',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontWeight: 700,
                }}
              >
                Note: During the temporary period, all courses are published as <strong>FREE</strong>. Pricing is locked by the platform.
              </p>
            )}
     
            <div className="insta-modal-buttons">
              {!PLATFORM_TEMPORARY_FREE_COURSES && (
                <button className="SetDefault-Price-btn" onClick={handleSetDefaultPrice}>
                  <FaHistory /> Reset to Default Price
                </button>
              )}
              <button className="save-btn" onClick={handleSubmitPayment}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCourses;